import type { components } from '@/api/qraft/schema';
import type {
  FilterField,
  FilterFieldType,
} from '~/styles/tables/filterandSettingsStyles';

type ListFilter = components['schemas']['ListFilter'];
type ListFilterControl = components['schemas']['ListFilterControl'];

/**
 * The API's control vocabulary mirrors `FilterFieldType` one-for-one. Spelling
 * the mapping out (rather than casting) means the build breaks if either side
 * ever grows a control the other doesn't have.
 */
const CONTROL_TO_FIELD_TYPE: Record<ListFilterControl, FilterFieldType> = {
  text: 'text',
  select: 'select',
  multiselect: 'multiselect',
  date: 'date',
  checkbox: 'checkbox',
};

/**
 * Per-filter presentation overrides, keyed by the server's filter key.
 *
 * A list's `label` column is seeded in English and served as-is in every
 * language, so pages supply their own translated `label` here. The *options*
 * are translated per user language, so those always come from the endpoint.
 */
export interface ListFilterOverride {
  label?: string;
  /** `checkbox` filters render nothing but a box without this. */
  checkboxLabel?: string;
  placeholder?: string;
  /**
   * Pair this field with another on one row — `TableFilterPanel` renders the
   * two as a range (e.g. the Last Activity after/before dates).
   */
  groupWith?: string;
  /**
   * Client-supplied options, preferred over the server's when present.
   *
   * For the closed vocabularies the server has no seeded option rows for — the
   * submissions Status filter's Graded/Failed pair, whose legacy labels are
   * catalogue units the frontend already carries as i18n keys. Everything whose
   * options are *data* (students, tags, quizzes, countries) must keep coming
   * from the endpoint.
   */
  options?: ReadonlyArray<{ value: string; label: string }>;
}

/**
 * Turn `GET /v3/lists/{list_key}/filters` metadata into the `filterFields`
 * `WbsTable` renders. The resulting field keys are the server's filter keys, so
 * the values the panel produces are already the list endpoint's query params.
 */
export function listFiltersToFilterFields(
  filters: readonly ListFilter[] | undefined,
  overrides: Record<string, ListFilterOverride> = {},
): FilterField[] {
  if (!filters) return [];
  return filters.map((filter) => {
    const override = overrides[filter.key];
    return {
      key: filter.key,
      label: override?.label ?? filter.label,
      type: CONTROL_TO_FIELD_TYPE[filter.type],
      options:
        override?.options !== undefined
          ? override.options.map((option) => ({
              value: option.value,
              label: option.label,
            }))
          : filter.options.length > 0
            ? filter.options.map((option) => ({
                value: option.value,
                label: option.label,
              }))
            : undefined,
      checkboxLabel: override?.checkboxLabel,
      placeholder: override?.placeholder,
      groupWith: override?.groupWith,
    };
  });
}
